import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { COMPANY, REGISTERED_OFFICE, REDRESS } from "../../constants/legal";
import NetopiaBadge from "./NetopiaBadge";
import { openCookieSettings } from "../../utils/cookieConsent";

/**
 * Footer Component
 * Displays footer with company info, links, services, badges, and copyright.
 *
 * There used to be a fifth column collecting newsletter sign-ups and a row of
 * social-media icons next to the company info — neither is used (the social
 * links were all placeholder "#" hrefs, no account behind them), so both were
 * dropped. The legal identity line moved up here from the bottom bar, next to
 * the same operator it identifies, and the badges that bar carried took the
 * newsletter's old column instead of sitting in a footnote.
 *
 * @param {Object} footerInfo - Footer information object
 * @param {Array} usefulLinks - Array of useful link objects
 * @param {Array} services - Array of service link objects
 */
const Footer = ({ footerInfo = {}, usefulLinks = [], services = [] }) => {
  const { t } = useTranslation();

  // The operator's real details, from the one place they are allowed to live.
  const defaultFooterInfo = {
    companyName: COMPANY.brandName,
    address: REGISTERED_OFFICE.street,
    city: REGISTERED_OFFICE.cityLine,
    phone: COMPANY.phone,
    email: COMPANY.email,
  };

  // Default useful links
  const defaultUsefulLinks = [
    { text: t("Footer.usefulLinks.home"), url: "#" },
    { text: t("Footer.usefulLinks.about"), url: "#about" },
    { text: t("Footer.usefulLinks.services"), url: "#services" },
    // Real routes, not "#": these two are legally required to be reachable, and
    // an anchor to nowhere is the same as not publishing them at all.
    { text: t("Footer.usefulLinks.termsOfService"), url: "/terms" },
    { text: t("Footer.usefulLinks.privacyPolicy"), url: "/privacy" },
    { text: t("Footer.usefulLinks.cookies"), url: "/cookies" },
    // The two NETOPIA asks for by name, and the one art. 11^1 of OUG 34/2014
    // requires be reachable online rather than by e-mail.
    { text: t("Footer.usefulLinks.delivery"), url: "/delivery" },
    { text: t("Footer.usefulLinks.cancellation"), url: "/cancellation" },
    { text: t("Footer.usefulLinks.withdrawal"), url: "/withdrawal" },
  ];

  // Default services
  const defaultServices = [
    { text: t("Footer.services.onlineBooking"), url: "#" },
    { text: t("Footer.services.resourceManagement"), url: "#" },
    { text: t("Footer.services.analytics"), url: "#" },
    { text: t("Footer.services.automatedReminders"), url: "#" },
    { text: t("Footer.services.customization"), url: "#" },
  ];

  const displayFooterInfo =
    Object.keys(footerInfo).length > 0 ? footerInfo : defaultFooterInfo;
  const displayUsefulLinks =
    usefulLinks.length > 0 ? usefulLinks : defaultUsefulLinks;
  const displayServices = services.length > 0 ? services : defaultServices;

  const scrollToTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <footer id="footer" className="bg-ink-900 text-ink-300">
        <div className="footer-top mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12">
            {/* Footer Info */}
            <div className="footer-info lg:col-span-4">
              <h3 className="text-xl font-bold text-white">
                {displayFooterInfo.companyName}
              </h3>
              {/* Who is actually selling, next to the brand a visitor knows.
                  Legea 365/2002 art. 5 requires this on the site, not only
                  inside a document somebody has to open, and NETOPIA's
                  point-of-sale review looks for the fiscal code here. */}
              <p className="company-identity mt-1.5 text-xs leading-relaxed text-ink-500">
                {COMPANY.legalName} · CUI {COMPANY.taxId} ·{" "}
                {COMPANY.tradeRegisterNumber}
              </p>
              <p className="mt-4 text-fluid-sm leading-relaxed text-ink-400">
                {displayFooterInfo.address}
                <br />
                {displayFooterInfo.city}
              </p>
              <p className="mt-4 space-y-1 text-fluid-sm text-ink-400">
                <span className="block">
                  <strong className="font-semibold text-ink-300">
                    {t("Footer.phone")}:
                  </strong>{" "}
                  {displayFooterInfo.phone}
                </span>
                <span className="block">
                  <strong className="font-semibold text-ink-300">
                    {t("Footer.email")}:
                  </strong>{" "}
                  {displayFooterInfo.email}
                </span>
              </p>
            </div>

            {/* Useful Links */}
            <div className="footer-links lg:col-span-2">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-white">
                {t("Footer.usefulLinksTitle")}
              </h4>
              <ul className="mt-4 space-y-2.5">
                {displayUsefulLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.url}
                      className="text-fluid-sm text-ink-400 transition hover:text-brand-400"
                      onClick={(e) => {
                        if (link.url === "#") e.preventDefault();
                      }}
                    >
                      {link.text}
                    </a>
                  </li>
                ))}
                {/* Not a link in the list above, and not overridable through
                    `usefulLinks`: this is how consent is withdrawn, which has to
                    stay as easy as giving it (GDPR art. 7(3)), so every page
                    with this footer keeps it. */}
                <li>
                  <button
                    type="button"
                    onClick={openCookieSettings}
                    className="cookie-settings-link cursor-pointer text-left text-fluid-sm text-ink-400 transition hover:text-brand-400"
                  >
                    {t("Footer.cookieSettings")}
                  </button>
                </li>
              </ul>
            </div>

            {/* Our Services */}
            <div className="footer-links lg:col-span-3">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-white">
                {t("Footer.servicesTitle")}
              </h4>
              <ul className="mt-4 space-y-2.5">
                {displayServices.map((service, index) => (
                  <li key={index}>
                    <a
                      href={service.url}
                      className="text-fluid-sm text-ink-400 transition hover:text-brand-400"
                      onClick={(e) => {
                        if (service.url === "#") e.preventDefault();
                      }}
                    >
                      {service.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Badges — the newsletter form used to sit here; nobody signed up
                for it, so the space went to the pictograms that used to be a
                footnote at the very bottom of the page instead. */}
            <div className="footer-badges lg:col-span-3">
              {/* ANPC's alternative dispute resolution pictogram, which traders
                  selling online must display (Ordinul ANPC 449/2022). The image
                  is the official one, served from our own domain so the badge
                  does not disappear when theirs moves. */}
              {/* NETOPIA first, then ANPC. Their approval notice asks for their
                  badge in the footer specifically, and putting it at the head
                  of the row is the difference between a reviewer finding it and
                  a reviewer scrolling past it. */}
              <div className="anpc-badges flex flex-wrap items-center gap-x-4 gap-y-3">
                <NetopiaBadge />

                <a
                  href={REDRESS.anpcSal}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("Footer.anpcSalAria")}
                >
                  <img
                    src="/images/anpc-sal.png"
                    alt={t("Footer.anpcSalAlt")}
                    width={201}
                    height={50}
                    loading="lazy"
                    className="h-[38px] w-auto opacity-90 transition hover:opacity-100"
                  />
                </a>
              </div>

              {/* There is no companion "SOL" badge: the European ODR platform it
                  pointed at closed on 20 July 2025 under Regulation (EU)
                  2024/3228. Most Romanian shops still show it linking to a
                  notice of its own closure — the Commission's list of dispute
                  bodies is linked in words instead, right under ANPC's own, which
                  is where consumers are actually sent now. */}
              <a
                href={REDRESS.euDisputeBodies}
                target="_blank"
                rel="noreferrer"
                className="mt-3 block text-xs text-ink-500 underline-offset-2 hover:text-ink-300 hover:underline"
              >
                {t("Footer.euDisputeBodies")}
              </a>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/10">
          <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
            <div className="copyright text-center text-fluid-sm text-ink-500">
              &copy;{" "}
              {t("Footer.copyright", {
                companyName: displayFooterInfo.companyName,
              })}
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <button
        type="button"
        className="back-to-top fixed bottom-5 right-5 z-40 inline-flex size-11 items-center justify-center rounded-full bg-brand-600 text-white shadow-brand transition hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        onClick={scrollToTop}
        aria-label={t("Common.backToTop")}
      >
        <i className="bi bi-arrow-up-short text-3xl" aria-hidden="true" />
      </button>
    </>
  );
};

Footer.propTypes = {
  footerInfo: PropTypes.shape({
    companyName: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
  }),
  usefulLinks: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ),
  services: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ),
};

export default Footer;
